import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getProducts } from "../../lib/catalogCache";
import api from "../../lib/api";
import PublicHeader from "../../components/layout/PublicHeader";
import PublicFooter from "../../components/layout/PublicFooter";
import PluginCard from "../../components/PluginCard";
import { StarDisplay } from "../../components/StarRating";
import { youtubeThumbnail, youtubeEmbedUrl } from "../../lib/youtube";

const DAWS = ["Pro Tools", "Logic Pro", "Ableton Live", "Cubase", "FL Studio", "Studio One"];
const TRUST = [
    { t: "Secure SSL payment", d: "Encrypted checkout" },
    { t: "Instant delivery", d: "Serial & download right away" },
    { t: "USB license", d: "Hardware key support" },
    { t: "Real support", d: "AVX Pro Chile team" },
];

const benefits = [
    { title: "Secure downloads", desc: "Protected, authenticated access per purchase." },
    { title: "Windows & macOS", desc: "Native installers for your DAW." },
    { title: "User dashboard", desc: "Manage your plugins from a single place." },
    { title: "Purchase history", desc: "Receipts and status always available." },
    { title: "Technical support", desc: "Direct tickets with the AVX Pro team." },
    { title: "Updates", desc: "Get notified when a new version is available." },
];

const faqs = [
    {
        q: "What do I need to use the plugins?",
        a: "A DAW compatible with VST3, AU or AAX, Windows 10/11 64-bit or macOS 11+, and at least 4 GB of RAM.",
    },
    {
        q: "How do I receive my installers?",
        a: "Once your purchase is registered and paid, the installers become available in your private dashboard under \"My Plugins\".",
    },
    {
        q: "Can I use it in the studio and live?",
        a: "Yes. The AVX Pro line is designed for mixing, mastering, broadcast and live sound.",
    },
    {
        q: "What happens when there is a new version?",
        a: "A notice appears in \"My Plugins\" indicating that a new version is available for download.",
    },
];

const compatibility = ["VST3", "AU", "AAX", "Windows", "macOS", "64-bit"];

export default function Home() {
    const [products, setProducts] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    useEffect(() => {
        getProducts().then((data) => setProducts(data || [])).catch(() => setProducts([]));
        api.get("/reviews/featured").then(({ data }) => setTestimonials(data || [])).catch(() => {});
    }, []);

    const featured = products.slice(0, 3);

    // Aggregate up to 6 videos across all published products for the home gallery.
    const videoEntries = [];
    for (const p of products) {
        for (const vid of p.youtube_videos || []) {
            videoEntries.push({ id: vid, product: p });
            if (videoEntries.length >= 6) break;
        }
        if (videoEntries.length >= 6) break;
    }
    const [playingId, setPlayingId] = useState(null);

    return (
        <div className="min-h-screen flex flex-col" data-testid="home-page">
            <PublicHeader />

            {/* HERO */}
            <section className="relative overflow-hidden border-b border-avx-line">
                <div className="absolute inset-0 opacity-50">
                    <img
                        src="https://images.unsplash.com/photo-1638784045911-a20d52ccf98e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
                        alt=""
                        fetchpriority="high"
                        decoding="async"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-avx-bg/70 via-avx-bg/85 to-avx-bg" />
                <div className="absolute inset-0 bg-avx-glow" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-36">
                    <div className="max-w-3xl animate-fade-up">
                        <div className="label-mono mb-6 flex items-center gap-3">
                            <span className="w-8 h-px bg-avx-blue" />
                            AVX Pro · v1.0
                        </div>
                        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mb-6">
                            Professional plugins for <span className="text-avx-blue">mixing, mastering</span> and live sound
                        </h1>
                        <p className="text-lg text-avx-muted leading-relaxed max-w-2xl mb-10">
                            AVX Pro processing designed for studio, FOH, broadcast and music production. Download, manage and update your plugins from a private dashboard.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/plugins" data-testid="hero-cta-plugins" className="btn-primary px-7 py-3 text-sm font-mono uppercase tracking-[0.2em] rounded-sm">
                                View plugins
                            </Link>
                            <Link to="/login" data-testid="hero-cta-login" className="btn-secondary px-7 py-3 text-sm font-mono uppercase tracking-[0.2em] rounded-sm">
                                Log in
                            </Link>
                        </div>
                        <div className="mt-12 flex items-center gap-2 flex-wrap">
                            {compatibility.map((c) => (
                                <span key={c} className="px-2.5 py-1 rounded-sm border border-avx-line bg-avx-surface/70 text-avx-text/90 text-[10px] font-mono uppercase tracking-[0.2em]">
                                    {c}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* DAW COMPATIBILITY + TRUST */}
            <section className="border-b border-avx-line bg-avx-surface/30" data-testid="daw-compat-row">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
                    <div className="flex items-center gap-x-8 gap-y-3 flex-wrap justify-center">
                        <span className="label-mono text-avx-muted">Works with</span>
                        {DAWS.map((d) => (
                            <span key={d} className="font-heading font-bold text-sm sm:text-base text-avx-text/60 hover:text-avx-blue transition-colors">{d}</span>
                        ))}
                    </div>
                    <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-avx-line rounded-sm overflow-hidden" data-testid="trust-badges">
                        {TRUST.map((b, i) => (
                            <div key={i} className="bg-avx-bg px-5 py-5 flex items-start gap-3">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-5 h-5 text-avx-blue shrink-0 mt-0.5"><path d="M9 12l2 2 4-4" /><path d="M12 3l7 4v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V7l7-4z" /></svg>
                                <div>
                                    <div className="font-heading font-bold text-sm">{b.t}</div>
                                    <div className="text-[11px] font-mono text-avx-muted mt-0.5">{b.d}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURED PLUGINS */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="label-mono mb-3">/ 01 · Catalog</div>
                        <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">Featured plugins</h2>
                    </div>
                    <Link to="/plugins" className="text-xs font-mono uppercase tracking-[0.2em] text-avx-blue hover:underline" data-testid="featured-see-all">
                        View all →
                    </Link>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {featured.length === 0 ? (
                        <div className="col-span-full text-avx-muted font-mono text-sm">Loading catalog…</div>
                    ) : (
                        featured.map((p) => <PluginCard key={p.id} product={p} />)
                    )}
                </div>
            </section>

            {/* TESTIMONIALS / SOCIAL PROOF */}
            {testimonials.length > 0 && (
                <section className="border-t border-avx-line py-20 lg:py-28" data-testid="testimonials-section">
                    <div className="max-w-7xl mx-auto px-6 lg:px-10">
                        <div className="label-mono mb-3">/ 01·8 · Reviews</div>
                        <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight mb-12 max-w-2xl">What producers are saying</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {testimonials.slice(0, 6).map((r) => (
                                <div key={r.id} className="panel-elevated p-6 flex flex-col" data-testid={`testimonial-${r.id}`}>
                                    <StarDisplay value={r.rating} size={15} showCount={false} />
                                    {r.title && <div className="font-heading font-bold text-base mt-3">{r.title}</div>}
                                    <p className="text-sm text-avx-muted leading-relaxed mt-2 flex-1">"{r.comment}"</p>
                                    <div className="mt-4 pt-4 border-t border-avx-line flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className="w-8 h-8 rounded-full bg-avx-blue/15 border border-avx-blue/40 flex items-center justify-center font-heading font-black text-xs text-avx-blue shrink-0">
                                                {(r.author_name || "A").charAt(0).toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-xs font-bold truncate">{r.author_name}</div>
                                                {r.verified && <div className="text-[9px] font-mono uppercase tracking-[0.15em] text-avx-success">Verified purchase</div>}
                                            </div>
                                        </div>
                                        <Link to={`/plugins/${r.product.slug}`} className="text-[10px] font-mono uppercase tracking-[0.15em] text-avx-blue hover:underline shrink-0">{r.product.name} →</Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* VIDEO GALLERY */}
            {videoEntries.length > 0 && (
                <section id="videos" className="border-t border-avx-line bg-avx-surface/40 py-20 lg:py-28" data-testid="home-video-gallery">
                    <div className="max-w-7xl mx-auto px-6 lg:px-10">
                        <div className="flex items-end justify-between mb-10">
                            <div>
                                <div className="label-mono mb-3">/ 01·5 · Videos</div>
                                <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">Hear the plugins in action</h2>
                            </div>
                            <Link to="/plugins" className="text-xs font-mono uppercase tracking-[0.2em] text-avx-blue hover:underline">
                                View all →
                            </Link>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {videoEntries.map(({ id, product }) => (
                                <div key={`${product.id}-${id}`} className="panel-elevated overflow-hidden flex flex-col" data-testid={`home-video-${id}`}>
                                    <div className="relative aspect-video bg-black">
                                        {playingId === id ? (
                                            <iframe
                                                src={`${youtubeEmbedUrl(id)}&autoplay=1`}
                                                title={product.name}
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                allowFullScreen
                                                className="w-full h-full"
                                            />
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => setPlayingId(id)}
                                                className="group absolute inset-0 w-full h-full"
                                                data-testid={`home-video-play-${id}`}
                                                aria-label={`Play ${product.name}`}
                                            >
                                                <img src={youtubeThumbnail(id)} alt={product.name} loading="lazy" className="w-full h-full object-cover opacity-85 group-hover:opacity-100 transition-opacity" />
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/15 transition-colors">
                                                    <div className="w-14 h-14 rounded-full bg-avx-blue/95 flex items-center justify-center shadow-[0_0_28px_rgba(63,169,255,0.5)]">
                                                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-avx-bg ml-0.5">
                                                            <path d="M8 5v14l11-7z" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </button>
                                        )}
                                    </div>
                                    <div className="p-4 flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="font-heading font-bold text-sm leading-tight truncate">{product.name}</div>
                                            <div className="label-mono mt-1">{product.slug}</div>
                                        </div>
                                        <Link
                                            to={`/plugins/${product.slug}`}
                                            className="shrink-0 text-[10px] font-mono uppercase tracking-[0.18em] text-avx-blue hover:underline"
                                        >
                                            View plugin →
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* BENEFITS */}
            <section id="beneficios" className="border-t border-avx-line bg-avx-surface/40 py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="label-mono mb-3">/ 02 · Benefits</div>
                    <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight mb-12 max-w-3xl">
                        Your AVX Pro ecosystem to download and manage your professional plugins
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-avx-line">
                        {benefits.map((b, i) => (
                            <div key={i} className="bg-avx-bg p-8 hover:bg-avx-surface/60 transition-colors">
                                <div className="font-mono text-xs text-avx-blue mb-3">/ {String(i + 1).padStart(2, "0")}</div>
                                <h3 className="font-heading font-bold text-lg mb-2">{b.title}</h3>
                                <p className="text-sm text-avx-muted leading-relaxed">{b.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
                <div className="label-mono mb-3">/ 03 · Workflow</div>
                <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight mb-12">How it works</h2>
                <div className="grid md:grid-cols-4 gap-6">
                    {[
                        { n: "01", t: "Create your account", d: "Sign up as an AVX Pro customer." },
                        { n: "02", t: "Make your purchase", d: "Your purchase is registered on the platform." },
                        { n: "03", t: "Access your dashboard", d: "Your plugins are ready to download." },
                        { n: "04", t: "Install and produce", d: "Download the installer for your system." },
                    ].map((s) => (
                        <div key={s.n} className="panel p-6 relative overflow-hidden">
                            <div className="font-heading font-black text-5xl text-avx-blue/15 absolute top-2 right-4 select-none">{s.n}</div>
                            <div className="label-mono mb-4">{s.n}</div>
                            <h3 className="font-heading font-bold text-base mb-2">{s.t}</h3>
                            <p className="text-sm text-avx-muted leading-relaxed">{s.d}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* SOBRE NOSOTROS */}
            <section id="sobre-nosotros" className="border-t border-avx-line bg-avx-surface/40 py-20 lg:py-28" data-testid="about-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-20 mb-16">
                        <div>
                            <div className="label-mono mb-3">/ 04 · AVX Pro Chile</div>
                            <h2 className="font-heading font-bold text-3xl lg:text-5xl tracking-tight leading-[1.05] mb-6">
                                About <span className="text-avx-blue">us</span>
                            </h2>
                            <div className="flex items-center gap-3 mb-6">
                                <span className="px-2.5 py-1 rounded-sm border border-avx-blue/40 bg-avx-blue/10 text-avx-blue text-[10px] font-mono uppercase tracking-[0.2em]">
                                    Made in Chile
                                </span>
                                <span className="px-2.5 py-1 rounded-sm border border-avx-line bg-avx-bg/50 text-avx-text/80 text-[10px] font-mono uppercase tracking-[0.2em]">
                                    Professional audio
                                </span>
                            </div>
                        </div>
                        <div className="space-y-5 text-base leading-relaxed text-avx-text/85">
                            <p>
                                AVX Pro Chile was born from real experience in professional audio, hands-on field work, and the need to create more direct, musical and functional tools for technicians, musicians, producers and sound engineers. Our approach comes from the world of live sound, mixing, monitoring, RF control and the development of solutions designed for those who work with audio every day.
                            </p>
                            <p>
                                We create plugins and platforms with a clear vision: uniting technology, sonic judgment and practical workflow. We don't seek to copy what already exists, but to develop tools with their own identity, designed to solve real problems in mixing, mastering, live and studio work, while keeping a clear interface, professional sound and simple yet effective decisions.
                            </p>
                            <p>
                                AVX Pro stands out because it is built from the reality of audio in the field. Every idea is born from a concrete need: better control, faster mixing, protecting the system, achieving sonic character and maintaining an organized workflow. Our goal is for every tool to feel useful, honest and ready for professional use.
                            </p>
                        </div>
                    </div>

                    {/* Mission + Vision */}
                    <div className="grid md:grid-cols-2 gap-6 mb-16" data-testid="mission-vision">
                        <div className="panel-elevated p-8 lg:p-10 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-1 h-full bg-avx-blue" />
                            <div className="label-mono mb-4 text-avx-blue">/ Mission</div>
                            <p className="font-heading font-bold text-xl lg:text-2xl tracking-tight leading-snug">
                                To create professional audio tools that unite innovation, sonic judgment and real field experience.
                            </p>
                        </div>
                        <div className="panel-elevated p-8 lg:p-10 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-1 h-full bg-avx-blue" />
                            <div className="label-mono mb-4 text-avx-blue">/ Vision</div>
                            <p className="font-heading font-bold text-xl lg:text-2xl tracking-tight leading-snug">
                                To make AVX Pro Chile a leading Latin American brand in plugins, platforms and technology for professional audio.
                            </p>
                        </div>
                    </div>

                    {/* Equipo */}
                    <div data-testid="team-section">
                        <div className="label-mono mb-3">/ Team</div>
                        <h3 className="font-heading font-bold text-2xl lg:text-3xl tracking-tight mb-8">Behind AVX Pro</h3>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="panel p-6 flex gap-5 items-start" data-testid="team-josue">
                                <div className="shrink-0 w-16 h-16 rounded-sm bg-avx-blue/15 border border-avx-blue/40 flex items-center justify-center font-heading font-black text-2xl text-avx-blue">
                                    JP
                                </div>
                                <div>
                                    <h4 className="font-heading font-bold text-lg leading-tight">Josue Pazmiño</h4>
                                    <div className="label-mono mb-3 mt-1">Director, AVX Pro Chile</div>
                                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-avx-blue mb-3">
                                        Sound concept · Product design · Musical decisions
                                    </div>
                                    <p className="text-sm text-avx-muted leading-relaxed">
                                        Drives the AVX Pro vision from experience in professional audio, live sound, mixing, RF and development of solutions designed for technicians, musicians and engineers.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="border-t border-avx-line bg-avx-surface/40 py-20 lg:py-28">
                <div className="max-w-4xl mx-auto px-6 lg:px-10">
                    <div className="label-mono mb-3">/ 05 · FAQ</div>
                    <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight mb-12">Frequently asked questions</h2>
                    <div className="space-y-3">
                        {faqs.map((f, i) => (
                            <details key={i} className="panel p-6 group" data-testid={`faq-${i}`}>
                                <summary className="cursor-pointer flex items-start justify-between gap-4 list-none">
                                    <span className="font-heading font-semibold text-base">{f.q}</span>
                                    <span className="text-avx-blue font-mono text-lg group-open:rotate-45 transition-transform shrink-0">+</span>
                                </summary>
                                <p className="mt-4 text-sm text-avx-muted leading-relaxed">{f.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
}
